import { useId, useMemo } from "react";
import { Code, Database, Package, SearchCode, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import type { WorkflowSpec } from "~/services/pinnedArtifacts";
import { layoutWorkflow, type LineageNode, type LineageNodeKind } from "./workflowGraph";

const NODE_STYLES: Record<LineageNodeKind, string> = {
  source:
    "rounded-lg border-neutral-400 bg-neutral-50 hover:border-neutral-600 dark:bg-neutral-900",
  query:
    "rounded-full border-teal-500 bg-teal-50 hover:bg-teal-100 dark:bg-teal-950/50 dark:hover:bg-teal-900/50",
  code: "rounded-full border-amber-500 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/50 dark:hover:bg-amber-900/50",
  intermediate: "rounded-lg border-blue-400 bg-blue-50 hover:border-blue-600 dark:bg-blue-950/50",
  artifact: "rounded-lg border-2 border-violet-600 bg-violet-50 dark:bg-violet-950/50",
};

const SUB_STYLES: Record<LineageNodeKind, string> = {
  source: "font-mono text-muted-foreground",
  query: "text-teal-700 dark:text-teal-300",
  code: "text-amber-700 dark:text-amber-300",
  intermediate: "font-mono text-blue-700 dark:text-blue-300",
  artifact: "font-mono text-violet-700 dark:text-violet-300",
};

const LEGEND: Array<{ kind: LineageNodeKind; label: string; swatch: string }> = [
  { kind: "source", label: "Source", swatch: "rounded-sm border-neutral-400 bg-neutral-50" },
  { kind: "query", label: "Query", swatch: "w-3.5 rounded-full border-teal-500 bg-teal-50" },
  { kind: "code", label: "Code", swatch: "w-3.5 rounded-full border-amber-500 bg-amber-50" },
  { kind: "intermediate", label: "Intermediate", swatch: "rounded-sm border-blue-400 bg-blue-50" },
  {
    kind: "artifact",
    label: "Artifact",
    swatch: "rounded-sm border-2 border-violet-600 bg-violet-50",
  },
];

const KIND_LABEL: Record<LineageNodeKind, string> = {
  source: "Source table",
  query: "Query · SQL",
  code: "Code",
  intermediate: "Intermediate artifact",
  artifact: "Final artifact",
};

const NodeIcon = ({ kind }: { kind: LineageNodeKind }) => {
  const className = "size-3 shrink-0";
  if (kind === "source") return <Database className={cn(className, "text-muted-foreground")} />;
  if (kind === "query") return <SearchCode className={className} />;
  if (kind === "code") return <Code className={className} />;
  if (kind === "artifact") return <Package className={cn(className, "text-violet-600")} />;
  return null;
};

interface LineageGraphProps {
  spec: WorkflowSpec;
  selectedId: string | null;
  onSelect: (node: LineageNode | null) => void;
}

/** The recipe drawn as a layered graph of sources, work and artifacts, with a
 *  detail card for the selected node. */
export const LineageGraph = ({ spec, selectedId, onSelect }: LineageGraphProps) => {
  const layout = useMemo(() => layoutWorkflow(spec), [spec]);
  const markerId = `lineage-arrow-${useId().replace(/:/g, "")}`;
  const selected = layout.nodes.find((n) => n.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-3 overflow-x-auto whitespace-nowrap text-[11px] text-muted-foreground">
        {LEGEND.map(({ kind, label, swatch }) => (
          <span key={kind} className="flex items-center gap-1.5">
            <span className={cn("h-2.5 w-3 border", swatch)} aria-hidden />
            {label}
          </span>
        ))}
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-background bg-[radial-gradient(var(--color-border)_1px,transparent_1px)] bg-[size:16px_16px] px-4 py-4">
        <div
          className="relative mx-auto"
          style={{ width: layout.width, height: layout.height }}
          role="group"
          aria-label="Lineage graph"
        >
          <svg
            width={layout.width}
            height={layout.height}
            className="absolute inset-0 overflow-visible"
            aria-hidden
          >
            <defs>
              <marker
                id={markerId}
                viewBox="0 0 8 8"
                refX="7"
                refY="4"
                markerWidth="7"
                markerHeight="7"
                orient="auto"
              >
                <path d="M0,0 L8,4 L0,8 z" className="fill-neutral-400" />
              </marker>
            </defs>
            <g fill="none" className="stroke-neutral-400" strokeWidth={1.25}>
              {layout.edges.map((edge) => (
                <path
                  key={`${edge.from}->${edge.to}`}
                  d={edge.path}
                  markerEnd={`url(#${markerId})`}
                />
              ))}
            </g>
          </svg>
          {layout.nodes.map((node) => (
            <button
              key={node.id}
              type="button"
              onClick={() => onSelect(node.id === selectedId ? null : node)}
              aria-pressed={node.id === selectedId}
              title={node.full}
              className={cn(
                "absolute flex cursor-pointer flex-col justify-center gap-0.5 border px-3 text-left",
                NODE_STYLES[node.kind],
                node.id === selectedId && "ring-2 ring-ring ring-offset-1"
              )}
              style={{ left: node.x, top: node.y, width: node.width, height: node.height }}
            >
              <span className="flex min-w-0 items-center gap-1.5 text-xs font-medium leading-4 text-foreground">
                <NodeIcon kind={node.kind} />
                <span className="truncate">{node.label}</span>
              </span>
              <span className={cn("truncate text-[10px] leading-[13px]", SUB_STYLES[node.kind])}>
                {node.sub}
              </span>
            </button>
          ))}
        </div>
      </div>
      {selected && <NodeDetail node={selected} onClear={() => onSelect(null)} />}
    </div>
  );
};

const nodeNote = (node: LineageNode): string => {
  const step = node.stepIndex === null ? null : node.stepIndex + 1;
  if (node.kind === "source") return "Read directly from the project dataset.";
  if (node.kind === "query") return `The query for step ${step} is open below.`;
  if (node.kind === "code") return `The code for step ${step} is open below.`;
  if (node.kind === "intermediate") return `Produced by step ${step} and reused downstream.`;
  return "The artifact this lineage traces.";
};

const NodeDetail = ({ node, onClear }: { node: LineageNode; onClear: () => void }) => (
  <div className="flex flex-col gap-1.5 rounded-lg border border-border px-3 py-2.5">
    <div className="flex items-center gap-2">
      <span className="rounded border border-border px-1.5 text-[11px] font-medium text-muted-foreground">
        {node.kind === "code" ? `Code · ${node.sub}` : KIND_LABEL[node.kind]}
      </span>
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{node.label}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onClear}
        aria-label="Clear selection"
        className="size-6 text-muted-foreground"
      >
        <X className="size-3.5" />
      </Button>
    </div>
    <code className="break-all font-mono text-[11px] leading-normal text-foreground/80">
      {node.full}
    </code>
    <span className="text-xs text-muted-foreground">{nodeNote(node)}</span>
  </div>
);
